import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { useProfile } from "../../hooks/useProfile";
import { apiFetch } from "../../context/AuthContext";
import AlertModal from "../../components/AlertModal";
import Mo3tiButton from "./components/layout/Mo3tiButton";

import translations from "./constants/translations";

// Hooks
import useResponsive from "./hooks/useResponsive";
import usePermissions from "./hooks/usePermissions";
import useVisibilityRules from "./hooks/useVisibilityRules";
import usePayrollData from "./hooks/usePayrollData";
import usePayrollFilters from "./hooks/usePayrollFilters";
import useAllowanceDeductionImport from "./hooks/useAllowanceDeductionImport";
import usePayrollExport from "./hooks/usePayrollExport";

// Layout & sections
import PayrollHeader from "./components/layout/PayrollHeader";
import ActionButtons from "./components/layout/ActionButtons";
import PayrollStatsGrid from "./components/stats/PayrollStatsGrid";
import PayrollFilters from "./components/filters/PayrollFilters";
import PayrollTable from "./components/table/PayrollTable";

// Modals
import ProcessPayrollModal from "./components/modals/ProcessPayrollModal/ProcessPayrollModal";
import RecreatePayrollModal from "./components/modals/RecreatePayrollModal";
import BulkUnpayModal from "./components/modals/BulkUnpayModal/BulkUnpayModal";
import MedicalInsuranceModal from "./components/modals/MedicalInsuranceModal/MedicalInsuranceModal";
import PayrollBulkActionBar from "./components/layout/PayrollBulkActionBar";
import BulkAllowanceModal from "./components/modals/BulkAdjustmentModal/BulkAllowanceModal";
import BulkDeductionModal from "./components/modals/BulkAdjustmentModal/BulkDeductionModal";
import DeductionsModal from "./components/modals/DeductionsModal/DeductionsModal";
import AllowancesModal from "./components/modals/AllowancesModal/AllowancesModal";
import EmployeeDetailsModal from "./components/modals/EmployeeDetailsModal/EmployeeDetailsModal";
import ExcelImportModal from "./components/modals/ExcelImportModal/ExcelImportModal";
import BenefitsModal from "./components/modals/BenefitsModal";
import AllowanceDeductionImportProgress from "./components/layout/AllowanceDeductionImportProgress";
import AllowanceDeductionImportResultsModal from "./components/modals/AllowanceDeductionImportResultsModal";
import { TemplateBuilderModal } from "./components/modals/TemplateBuilderModal";
import PayrollChoiceModal from "./components/modals/PayrollChoiceModal";
import DownloadMenuButton from "./components/layout/DownloadMenuButton";
import { SkeletonStyles, SkeletonBox, LoadingPill, SkeletonStatCard } from "../../components/SkeletonLoader";

import templateFile from "../../Documents/payroll_template.xlsx";
import { getCardStyle, getTopBarButtonStyle } from "../(Admin)Attendance/styles/attendanceStyles";
import {
  CreditCard,
  Upload,
  Download,
  Heart,
  RotateCcw,
  RefreshCw,
  FileSpreadsheet,
} from "lucide-react";

const ProfessionalPayrollSystem = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();
  const t = useCallback((key) => translations[language][key] || key, [language]);
  const isRTL = language === "ar";

  const itemsPerPage = 10;
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const clientName = profile?.clientName || localStorage.getItem("clientName");
  const workingCompany =
    profile?.workingCompany || localStorage.getItem("workingCompany");
  // Admins/superadmins always get full module access on the backend
  // (resolveUserPermissionsUncached returns allModulesAllowed:true for
  // them) — this local fallback keeps the action panel visible even if the
  // permissions fetch fails or falls back to a differently-shaped response,
  // matching the same isAdmin escape hatch already used in PayrollTable.jsx.
  const isAdmin = profile?.role === "admin" || profile?.role === "superadmin";

  const currentTheme = {
    colors: {
      primary: theme === "dark" ? "#2e7cba" : "#2e7cba",
      success: "#10b981",
      danger: "#ef4444",
      warning: "#f59e0b",
      info: "#2e7cba",
      textPrimary: theme === "dark" ? "#ffffff" : "#1a1a1a",
      textSecondary:
        theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
      textMuted:
        theme === "dark" ? "rgba(255, 255, 255, 1)" : "rgba(0,0,0,0.5)",
      cardBackground:
        theme === "dark" ? "rgba(30, 41, 59, 1.6)" : "rgba(255, 255, 255, 0.9)",
      inputBackground:
        theme === "dark"
          ? "rgba(30, 41, 59, 2.8)"
          : "rgba(255, 255, 255, 0.95)",
      border: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      borderLight:
        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
    },
    shadows: {
      card:
        theme === "dark"
          ? "0 8px 32px rgba(0,0,0,0.3)"
          : "0 8px 32px rgba(0,0,0,0.1)",
    },
  };

  // ───────────────────────── Hooks ─────────────────────────
  const { screenSize, spacing, fonts, container, gridCols } = useResponsive();
  const { modulePermissions, loadingPermissions } = usePermissions({ profile });
  const { visibilityRules, fetchVisibilityRules } = useVisibilityRules();

  // AlertModal state
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
    showCancel: false,
  });
  const showAlert = useCallback((title, message, variant = "info", showCancel = false) => {
    setAlertModal({ open: true, title, message, variant, showCancel });
  }, []);
  const closeAlert = () => {
    setAlertModal({ ...alertModal, open: false });
  };

  // ─── STEP 1: Single filter instance (starts with empty data) ───
  // selectedMonth lives here and is the source of truth for the API call.
  const filters = usePayrollFilters({
    t,
    currentTheme,
    payrollData: [], // placeholder — real data injected below via setPayrollData pattern
  });

  // ─── Notification deep-link (?employee=&year=&month=) ───
  // Seed the search box with the employee's infinityCode and jump to the
  // payroll period the adjustment belongs to.
  const [searchParams] = useSearchParams();
  const deepLinkConsumedRef = useRef(false);
  useEffect(() => {
    if (deepLinkConsumedRef.current) return;
    const employee = searchParams.get("employee");
    const year = Number(searchParams.get("year"));
    const month = Number(searchParams.get("month"));
    if (!employee && !year) return;
    deepLinkConsumedRef.current = true;
    if (employee) filters.setSearchTerm(employee);
    if (year && month) filters.setSelectedMonth({ year, month });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ─── STEP 2: Fetch payroll data using selectedMonth from the filter instance ───
  const payrollDataHook = usePayrollData({
    selectedMonth: filters.selectedMonth, // ✅ correct — same instance the UI updates
    statusFilter: filters.statusFilter,
    selectedDepartment: filters.selectedDepartment,
    selectedCompany: filters.selectedCompany,
    selectedLaborMode: filters.selectedLaborMode,
    searchTerm: filters.searchTerm,
    visibilityRules,
    fetchVisibilityRules,
    profile,
    clientName,
    language,
  });

  // ─── STEP 3: server-side filtering / pagination ───
  // Rows arrive already filtered, sorted and paginated by the server
  // (/payrollX/visible): `filteredPayrollData` IS the current page. Totals,
  // status counts and the filter options come from /visible/summary, so the
  // browser never holds the whole month.
  const filteredPayrollData = payrollDataHook.payrollData || [];
  const { pagination, summary } = payrollDataHook;
  const filteredTotal = summary?.filteredTotal ?? pagination?.total ?? 0;

  // ─── Row selection (table checkboxes + the Actions-bar "select all" tool) ───
  // Kept as its own Set, deliberately separate from the pre-existing
  // `selectedEmployees` state below — that one belongs to ProcessPayrollModal,
  // which already has its own self-contained department/company selection UI.
  // This one drives BULK versions of the existing row actions (Ignore
  // Attendance, Unpay) straight from the Actions bar, and always operates
  // against `filteredPayrollData` (every filtered row, not just the 10 shown
  // on the current table page) so "select all" is pagination-proof.
  // Selection is a map id → { id, status } so "select all" can cover rows on
  // pages that are not loaded: the ids come from /visible/ids, not the table.
  const [selectedMeta, setSelectedMeta] = useState(() => new Map());
  const selectedPayrollIds = React.useMemo(() => new Set(selectedMeta.keys()), [selectedMeta]);
  const [selectAllBusy, setSelectAllBusy] = useState(false);

  const toggleSelectPayroll = (id) => {
    setSelectedMeta((prev) => {
      const next = new Map(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        const row = filteredPayrollData.find((e) => e.id === id);
        next.set(id, {
          id,
          status: row?.status,
          infinityCode: row?.infinityCode,
          isIgnored: !!row?.isIgnored,
        });
      }
      return next;
    });
  };

  const allFilteredSelected = filteredTotal > 0 && selectedMeta.size >= filteredTotal;
  const someFilteredSelected = selectedMeta.size > 0 && !allFilteredSelected;

  const handleToggleSelectAllFiltered = async () => {
    if (allFilteredSelected) {
      setSelectedMeta(new Map());
      return;
    }
    if (selectAllBusy) return;
    setSelectAllBusy(true);
    try {
      const rows = await payrollDataHook.fetchFilteredIds();
      setSelectedMeta(
        new Map(
          rows.map((r) => [
            r.id,
            { id: r.id, status: r.paymentStatus, infinityCode: r.infinityCode, isIgnored: !!r.isIgnored },
          ]),
        ),
      );
    } catch (err) {
      showAlert(t("error"), err.message, "error");
    } finally {
      setSelectAllBusy(false);
    }
  };
  const handleClearPayrollSelection = () => setSelectedMeta(new Map());

  // A filter change changes which rows even exist in the filtered set —
  // drop the selection rather than keep ids that no longer match.
  useEffect(() => {
    setSelectedMeta(new Map());
  }, [
    filters.searchTerm,
    filters.statusFilter,
    filters.selectedDepartment,
    filters.selectedCompany,
    filters.selectedLaborMode,
    filters.selectedMonth,
  ]);

  // Selection entries are snapshots taken at selection time. When the loaded
  // page refreshes (a single-row Ignore/Unpay, a bulk allowance, …) refresh
  // the snapshot of every selected row on that page, so the bulk bar's
  // "ignored" / "paid" state never goes stale while the selection is kept.
  useEffect(() => {
    setSelectedMeta((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);
      for (const row of filteredPayrollData) {
        const cur = next.get(row.id);
        if (!cur) continue;
        const isIgnored = !!row.isIgnored;
        if (cur.isIgnored !== isIgnored || cur.status !== row.status) {
          next.set(row.id, { ...cur, isIgnored, status: row.status });
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [filteredPayrollData]);

  // { id, status, infinityCode, isIgnored } for every selected row — what the bulk
  // actions need, whether or not the row is on the loaded page.
  const selectedPayrollData = React.useMemo(() => Array.from(selectedMeta.values()), [selectedMeta]);

  // Totals for the WHOLE filtered set, summed by the server (not just the page).
  const totalGrossSalary = Number(summary?.totals?.grossSalary) || 0;
  const totalNetSalary = Number(summary?.totals?.netSalary) || 0;
  const totalDeductions = Number(summary?.totals?.totalDeductions) || 0;

  // salaryStats: month-wide status counts and filtered totals, from the summary
  const salaryStats = React.useMemo(
    () => [
      {
        icon: require("lucide-react").DollarSign,
        value: `${totalGrossSalary.toLocaleString()} ${t("currency")}`,
        label: t("totalGross"),
        color: "#0004f1c4",
        //subtitle: t("monthlyGross"),
      },
      {
        icon: require("lucide-react").UserCheck,
        value: String(summary?.statusCounts?.paid ?? 0),
        label: t("employeesPaid"),
        color: currentTheme.colors.success,
        //subtitle: t("currentMonth"),
      },
      {
        icon: require("lucide-react").Clock,
        value: String(summary?.statusCounts?.pending ?? 0),
        label: t("pendingPayments"),
        color: currentTheme.colors.warning,
        //subtitle: t("awaitingApproval"),
      },
      {
        icon: require("lucide-react").TrendingDown,
        value: `${totalDeductions.toLocaleString()} ${t("currency")}`,
        label: t("averageKPI"),
        color: currentTheme.colors.danger,
       // subtitle: t("performanceIndicator"),
      },
    ],
    [summary, totalGrossSalary, totalDeductions, t, currentTheme],
  );

  const {
    handleOpenAllowanceDeductionImport,
    handleAllowanceDeductionFileSelect,
    adImportProgress,
    handleMinimizeAdImportProgress,
    handleExpandAdImportProgress,
    handleCloseAdImportProgress,
    adImportResultsOpen,
    adImportResultsData,
    closeAdImportResults,
  } = useAllowanceDeductionImport({
    t,
    showAlert,
    refreshData: payrollDataHook.refreshData,
  });

  const { handleExportPayrollExcel, handleExportPayrollPDF } = usePayrollExport(
    {
      t,
      isRTL,
      language,
      selectedMonth: filters.selectedMonth,
      filteredPayrollData,
      fetchAllFiltered: payrollDataHook.fetchAllFiltered,
      payrollData: payrollDataHook.payrollData,
      totalGrossSalary,
      totalNetSalary,
      totalDeductions,
      showAlert,
    },
  );

  // ─── Bulk row actions driven by the table selection ───────────────────────
  // Mirrors the two per-row buttons in PayrollTable exactly (Ignore
  // Attendance's toggle, Unpay) — just applied to every selected employee at
  // once instead of one row at a time. Deliberately reuses the same
  // payrollDataHook handlers the row buttons call, so there is exactly one
  // code path for each action regardless of whether it's triggered from a
  // row or from the selection.
  const [bulkIgnoreBusy, setBulkIgnoreBusy] = useState(false);
  const [bulkUnpayBusy, setBulkUnpayBusy] = useState(false);

  const handleBulkToggleIgnore = async (targetState = true) => {
    // Only send the rows whose state actually changes — rows already in the
    // target state would just be reprocessed for nothing (mixed selections).
    const targets = selectedPayrollData.filter((emp) => !!emp.isIgnored !== targetState);
    if (targets.length === 0 || bulkIgnoreBusy) return;
    setBulkIgnoreBusy(true);
    try {
      await payrollDataHook.handleBulkToggleIsIgnored(
        targets.map((emp) => emp.id),
        targetState,
      );
      showAlert(
        t("success"),
        targetState
          ? `${isRTL ? "تم تجاهل الحضور لـ" : "Ignored attendance for"} (${targets.length})`
          : `${isRTL ? "تم إلغاء تجاهل الحضور لـ" : "Restored attendance for"} (${targets.length})`,
        "success",
      );
      handleClearPayrollSelection();
    } catch (err) {
      showAlert(t("error"), t("ignoreAttendanceError"), "error");
    } finally {
      setBulkIgnoreBusy(false);
    }
  };

  const handleBulkUnpay = async () => {
    const payableSelected = selectedPayrollData.filter(
      (e) => e.status === "paid",
    );
    if (payableSelected.length === 0 || bulkUnpayBusy) return;
    if (
      !window.confirm(
        `${t("unpayConfirm")} (${payableSelected.length} ${t("employees")})`,
      )
    ) {
      return;
    }
    setBulkUnpayBusy(true);
    try {
      // One request for the whole batch — see handleBulkUnpayPayroll in
      // usePayrollData for why this replaced firing the single-row unpay
      // endpoint once per selected employee.
      const result = await payrollDataHook.handleBulkUnpayPayroll(
        payableSelected.map((emp) => emp.id),
      );
      if (!result?.skipped) {
        showAlert(t("success"), `${t("unpaySuccess")} (${result?.updated ?? 0})`, "success");
      } else {
        showAlert(
          t("success"),
          `${t("unpaySuccess")}: ${result.updated}/${payableSelected.length}`,
          result.updated > 0 ? "success" : "error",
        );
      }
      handleClearPayrollSelection();
    } catch (err) {
      showAlert(t("error"), t("unpayError"), "error");
    } finally {
      setBulkUnpayBusy(false);
    }
  };

  // Visibility rules: fetch on mount
  useEffect(() => {
    fetchVisibilityRules();
  }, []);

  // ─── Skeleton فقط في أول تحميل — مش هيظهر تاني مع كل ريفريش ─────────────────
  const isInitialLoading = payrollDataHook.loading || loadingPermissions;
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!isInitialLoading) setPageReady(true);
  }, [isInitialLoading]);

  // ───────────────────── UI state ─────────────────────
  const [showTaxDetails, setShowTaxDetails] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processingPayroll, setProcessingPayroll] = useState(false);
  const [processStep, setProcessStep] = useState(1);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState(1);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  // Every import starts clean: the step, file, parsed rows, results, errors
  // and progress live here (above the modal), so they used to survive closing
  // the modal — reopening it showed the last import's results until the page
  // was reloaded.
  const resetImportState = () => {
    setImportStep(1);
    setImportFile(null);
    setImportData(null);
    setImportLoading(false);
    setImportError(null);
    setImportProgress(0);
    setImportResults(null);
  };
  const [importSettings, setImportSettings] = useState({
    updateExisting: false,
    skipErrors: true,
    calculateMissing: true,
    defaultYear: new Date().getFullYear(),
    defaultMonth: new Date().getMonth() + 1,
  });
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [showMedicalInsuranceModal, setShowMedicalInsuranceModal] =
    useState(false);
  const [showBulkUnpayModal, setShowBulkUnpayModal] = useState(false);
  const [showRecreateModal, setShowRecreateModal] = useState(false);
  const [recreateInitialCodes, setRecreateInitialCodes] = useState([]);
  const [showTemplateDetailsModal, setShowTemplateDetailsModal] =
    useState(false);
  // Which template the shared Template Builder is building.
  const [templateBuilderMode, setTemplateBuilderMode] = useState("allowancesDeductions");
  const [showTemplateBuilderModal, setShowTemplateBuilderModal] =
    useState(false);
  const [showImportDetailsModal, setShowImportDetailsModal] = useState(false);
  const [deductionTooltip, setDeductionTooltip] = useState({
    visible: false,
    employeeId: null,
    content: "",
    position: { x: 0, y: 0 },
  });
  const [deductionModal, setDeductionModal] = useState({
    isOpen: false,
    employee: null,
  });
  const [allowanceModal, setAllowanceModal] = useState({
    isOpen: false,
    employee: null,
  });
  const [showBulkAllowanceModal, setShowBulkAllowanceModal] = useState(false);
  const [showBulkDeductionModal, setShowBulkDeductionModal] = useState(false);
  // Process Payroll and Bulk Unpay work on the caller's WHOLE visibility
  // scope for the month (every company, every page), not on the 10 rows on
  // screen. Loaded when either modal opens and again after a refresh.
  const [monthRows, setMonthRows] = useState([]);
  const [monthRowsLoading, setMonthRowsLoading] = useState(false);
  const monthModalOpen = showProcessModal || showBulkUnpayModal;
  useEffect(() => {
    if (!monthModalOpen) return undefined;
    let cancelled = false;
    setMonthRowsLoading(true);
    payrollDataHook
      .fetchAllFiltered({ monthOnly: true })
      .then((rows) => {
        if (!cancelled) setMonthRows(rows);
      })
      .catch((err) => {
        if (!cancelled) showAlert(t("error"), err.message, "error");
      })
      .finally(() => {
        if (!cancelled) setMonthRowsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthModalOpen, payrollDataHook.refreshTrigger, filters.selectedMonth]);

  // Pagination is server-side: the table shows the current page and asks
  // the hook for another one.
  const currentItems = filteredPayrollData;
  const hasMore = (pagination?.page || 1) < (pagination?.totalPages || 1);

  const handleShowMore = () => {
    if (hasMore) payrollDataHook.setPage((pagination?.page || 1) + 1);
  };

  const hasRowActions = Boolean(
    isAdmin ||
    modulePermissions?.employeeDetails ||
    modulePermissions?.employeeDeductions ||
    modulePermissions?.employeeAllowances || // ← السطر الجديد
    modulePermissions?.ignoreAttendance ||
    (modulePermissions?.unpayPayroll ?? modulePermissions?.processPayroll), // row Unpay button
  );

  const hasActionPanel = Boolean(
    isAdmin ||
    modulePermissions?.processPayroll ||
    (modulePermissions?.unpayPayroll ?? modulePermissions?.processPayroll) ||
    modulePermissions?.dataImport ||
    modulePermissions?.dataExport ||
    modulePermissions?.medicalInsurance,
  );

  // ── registry "view" section gate (Admin View only) ─────────────────────
  // Page-level access is PageGuard's job; this enforces the payroll "view"
  // feature itself, matching the attendance/employees access-denied pattern.
  // `modulePermissions.view` is only defined when an admin level's features
  // are explicitly configured — every legacy/bypass path (admins, managers,
  // company-config fallback) leaves it undefined → allowed.
  const payrollViewType = (localStorage.getItem("managerViewType") || localStorage.getItem("viewType") || "manager").toLowerCase();
  const canViewPayroll = payrollViewType !== "admin" || (modulePermissions?.view ?? true);

  const handleExportAllowanceDeductionTemplate = async () => {
    // Fetched from the backend (built with exceljs) instead of generated
    // client-side — the frontend's "xlsx" package can't write cell fill
    // colors, and this template needs its allowance columns colored green
    // and its deduction columns colored red.
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/bulk/allowances-deductions-template`,
        { method: "GET" },
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch template: ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "allowances_deductions_template.xlsx";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showAlert(t("error"), t("allowanceDeductionImportError"), "error");
    }
  };

  const handleShowTemplateDetails = () => setShowTemplateDetailsModal(true);
  const handleCloseTemplateDetails = () => setShowTemplateDetailsModal(false);
  const handleShowImportDetails = () => setShowImportDetailsModal(true);
  const handleCloseImportDetails = () => setShowImportDetailsModal(false);

  const handleDownloadTemplate = async () => {
    const templateUrl =
      typeof templateFile === "string" ? templateFile : templateFile?.default;
    if (!templateUrl) {
      showAlert(t("error"), t("templateFileNotFound"), "error");
      return;
    }
    try {
      const response = await fetch(templateUrl);
      if (!response.ok)
        throw new Error(`Failed to fetch template: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "payroll_template.xlsx";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Template download failed:", error);
      showAlert(
        t("error"),
        t("errorDownloadingTemplate"),
        "error",
      );
    }
  };

  const handleDownloadAllTemplates = () => {
    handleDownloadTemplate();
    handleExportAllowanceDeductionTemplate();
    setShowTemplateDetailsModal(false);
  };

  // Modal body scroll lock
  useEffect(() => {
    const anyModalOpen =
      showProcessModal ||
      showImportModal ||
      selectedEmployee ||
      showTemplateDetailsModal ||
      showTemplateBuilderModal ||
      showImportDetailsModal ||
      deductionModal?.isOpen === true ||
      allowanceModal?.isOpen === true;

    if (anyModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "visible";
      document.body.style.paddingRight = "0px";
    }
    return () => {
      document.body.style.overflow = "visible";
      document.body.style.paddingRight = "0px";
    };
  }, [
    showProcessModal,
    showImportModal,
    selectedEmployee,
    deductionModal,
    showTemplateDetailsModal,
    showTemplateBuilderModal,
    showImportDetailsModal,
    allowanceModal,
  ]);

  const getResponsiveStyles = () => ({
    header: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
      borderRadius:
        screenSize === "xs" ? "1rem" : screenSize === "sm" ? "1.25rem" : "1rem",
      padding: spacing.padding,
      marginTop: -40,
      marginBottom: spacing.margin,
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
          : "0 0.5rem 2rem rgba(0,0,0,0.1)",
      backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.05)",
    },
    container: {
      marginTop: -20,
      direction: language === "ar" ? "rtl" : "ltr",
    },
    statCard: {
      background: theme === "dark" ? "transparent" : "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.5rem",
      padding: spacing.padding,
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.05)",
      boxShadow:
        theme === "dark"
          ? "0 8px 32px rgba(0,0,0,0.3)"
          : "0 8px 32px rgba(0,0,0,0.1)",
      cursor: "pointer",
    },
    welcomeSection: {},
    welcomeTitle: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: "28px",
      fontWeight: "700",
      textShadow:
        theme === "dark"
          ? "0 0.125rem 0.25rem rgba(0,0,0,0.3)"
          : "0 0.125rem 0.25rem rgba(0,0,0,0.1)",
    },
    primaryBtn: {
      background: "#2e7cba",
      color: "white",
      border: "none",
      padding: screenSize === "xs" ? "0.55rem 0.9rem" : "0.65rem 1.1rem",
      borderRadius: screenSize === "xs" ? "0.65rem" : "0.85rem",
      fontSize: "0.92rem",
      fontWeight: "700",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "0.4rem",
      boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.24)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    secondaryBtn: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
      color: theme === "dark" ? "white" : "#1a1a1a",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.2)"
          : "1px solid rgba(0,0,0,0.1)",
      padding: screenSize === "xs" ? "0.55rem 0.9rem" : "0.65rem 1.1rem",
      borderRadius: screenSize === "xs" ? "0.65rem" : "0.85rem",
      fontSize: "0.92rem",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "0.4rem",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    filterContainer: {
      display: "grid",
      gridTemplateColumns:
        screenSize === "xs"
          ? "1fr"
          : screenSize === "sm"
            ? "repeat(2, 1fr)"
            : screenSize === "md"
              ? "repeat(3, 1fr)"
              : "repeat(4, 1fr)",
      gap: spacing.gap,
      marginBottom: spacing.margin,
      padding: spacing.padding,
      background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.5)",
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.05)",
    },
    filterGroup: {
      display: "flex",
      flexDirection: "column",
      gap: spacing.small,
    },
    filterLabel: {
      color:
        theme === "dark"
          ? "rgba(248, 250, 252, 0.9)"
          : "rgba(30, 41, 59, 0.85)",
      fontSize: fonts.small,
      fontWeight: "500",
      marginBottom: spacing.xs,
    },
    filterInput: {
      width: "100%",
      padding: screenSize === "xs" ? "0.5rem 0.75rem" : "0.625rem 0.875rem",
      fontSize: fonts.body,
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.75rem",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(148, 163, 184, 0.2)",
      background:
        theme === "dark" ? "rgba(30, 41, 59, 0.4)" : "rgba(255, 255, 255, 0.8)",
      color:
        theme === "dark"
          ? "rgba(248, 250, 252, 0.95)"
          : "rgba(30, 41, 59, 0.9)",
      outline: "none",
      transition: "all 0.3s ease",
    },
    tableContainer: {
      overflowX: "auto",
      overflowY: "hidden",
      marginTop: spacing.margin,
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      position: "relative",
      background:
        theme === "dark" ? "rgba(30, 41, 59, 0.4)" : "rgba(255, 255, 255, 0.8)",
      ...(screenSize === "xs" && {
        maxWidth: "100%",
        WebkitOverflowScrolling: "touch",
      }),
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
      minWidth:
        screenSize === "xs" ? "600px" : screenSize === "sm" ? "700px" : "900px",
      position: "relative",
      zIndex: 1,
    },
    tableHeader: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      padding:
        screenSize === "xs"
          ? "0.75rem 0.5rem"
          : screenSize === "sm"
            ? "1rem 0.75rem"
            : "1.25rem 1rem",
      textAlign: "center",
      fontSize: fonts.tiny,
      fontWeight: "700",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      borderBottom:
        theme === "dark"
          ? "2px solid rgba(59, 130, 246, 0.3)"
          : "2px solid rgba(59, 130, 246, 0.1)",
      whiteSpace: "nowrap",
      position: "sticky",
      top: 0,
      zIndex: 10,
      backdropFilter: "blur(0.625rem)",
      boxShadow:
        theme === "dark"
          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
      transition: "all 0.3s ease",
    },
    tableCell: {
      padding:
        screenSize === "xs"
          ? "0.75rem 0.5rem"
          : screenSize === "sm"
            ? "1rem 0.75rem"
            : "1.25rem 1rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.06)"
          : "1px solid rgba(148, 163, 184, 0.08)",
      color:
        theme === "dark"
          ? "rgba(248, 250, 252, 0.95)"
          : "rgba(30, 41, 59, 0.9)",
      fontSize: fonts.body,
      fontWeight: "500",
      verticalAlign: isRTL ? "right" : "left",
      justifyContent: isRTL ? "right" : "left",
      textAlign: isRTL ? "right" : "left",
      position: "relative",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      background: "transparent",
    },
  });

  const styles = getResponsiveStyles();

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until the first successful load
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && isInitialLoading) {
    return (
      <div style={styles.container}>
        <SkeletonStyles theme={theme} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل بيانات المرتبات..." : "Loading payroll information..."}
          theme={theme}
          isRTL={isRTL}
        />

        {/* Header skeleton — styles.header carries a large negative marginTop
            meant to tuck it under the topbar; neutralize it here so it
            doesn't slide up over the LoadingPill above it. */}
        <div style={{ ...styles.header, marginTop: 0, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "24px" }}>
          <div>
            <SkeletonBox width="260px" height="32px" style={{ marginBottom: "12px" }} />
            <SkeletonBox width="340px" height="18px" />
          </div>
          <SkeletonBox width="48px" height="48px" style={{ borderRadius: "12px" }} />
        </div>

        {/* Stats skeleton */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              screenSize === "xs" ? "1fr" : screenSize === "md" ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
            gap: screenSize === "xs" ? "12px" : "20px",
            marginBottom: spacing.margin,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatCard key={i} cardStyle={{ ...styles.statCard, marginBottom: 0 }} />
          ))}
        </div>

        {/* Action buttons skeleton */}
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: spacing.margin }}>
          {[1, 2, 3, 4].map((i) => (
            <SkeletonBox key={i} width="150px" height="42px" style={{ borderRadius: "10px" }} />
          ))}
        </div>

        {/* Filters + table skeleton */}
        <div style={styles.statCard}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "12px",
              marginBottom: spacing.margin,
            }}
          >
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonBox key={i} width="100%" height="42px" style={{ borderRadius: "10px" }} />
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <SkeletonBox width="100%" height="44px" style={{ borderRadius: "8px" }} />
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonBox key={i} width="100%" height="56px" style={{ borderRadius: "8px" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── access denied: payroll "view" feature disabled (Admin View only) ──
  if (!loadingPermissions && !canViewPayroll) {
    return (
      <div style={styles.container}>
        <div
          style={{
            ...getCardStyle(theme),
            marginTop: 0,
            padding: "3rem 2rem",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              color: currentTheme.colors.textPrimary,
              fontSize: "1.25rem",
              marginBottom: "0.75rem",
            }}
          >
            {isRTL ? "غير مصرح" : "Access denied"}
          </h2>
          <p style={{ color: currentTheme.colors.textSecondary }}>
            {isRTL
              ? "ليس لديك إذن لعرض صفحة الرواتب."
              : "You do not have permission to view payroll."}
          </p>
        </div>
      </div>
    );
  }

  // ───────────────────────── Render ─────────────────────────
  return (
    <div>
      <PayrollHeader
        styles={styles} 
        t={t}
        translations={translations}
        language={language}
        theme={theme}
        setShowBenefitsModal={setShowBenefitsModal}
      />

      <PayrollStatsGrid
        salaryStats={salaryStats}
        theme={theme}
        spacing={spacing}
        fonts={fonts}
        screenSize={screenSize}
        styles={styles}
      />

      {/* <BenefitsModal
        t={t}
        theme={theme}
        currentTheme={currentTheme}
        showBenefitsModal={showBenefitsModal}
        setShowBenefitsModal={setShowBenefitsModal}
      /> */}

      {/* ── Unified Layout (same as EmployeeTable_Final) ──────────── */}
      <div style={{ marginTop: "24px", display: "flex", flexDirection: "column", gap: "24px", marginBottom: "24px" }}>
        {/* Filter Card */}
        <div style={{ ...getCardStyle(theme), marginTop: "0px", marginBottom: "0px" }}>
          <PayrollFilters
            t={t}
            styles={styles}
            currentTheme={currentTheme}
            theme={theme}
            language={language}
            payrollData={payrollDataHook.payrollData}
            searchTerm={filters.searchTerm}
            setSearchTerm={filters.setSearchTerm}
            statusFilter={filters.statusFilter}
            setStatusFilter={filters.setStatusFilter}
            statusSearch={filters.statusSearch}
            setStatusSearch={filters.setStatusSearch}
            selectedDepartment={filters.selectedDepartment}
            setSelectedDepartment={filters.setSelectedDepartment}
            departmentSearch={filters.departmentSearch}
            setDepartmentSearch={filters.setDepartmentSearch}
            selectedCompany={filters.selectedCompany}
            setSelectedCompany={filters.setSelectedCompany}
            companySearch={filters.companySearch}
            setCompanySearch={filters.setCompanySearch}
            selectedLaborMode={filters.selectedLaborMode}
            setSelectedLaborMode={filters.setSelectedLaborMode}
            laborModeSearch={filters.laborModeSearch}
            setLaborModeSearch={filters.setLaborModeSearch}
            selectedMonth={filters.selectedMonth}
            setSelectedMonth={filters.setSelectedMonth}
            yearSearch={filters.yearSearch}
            setYearSearch={filters.setYearSearch}
            monthSearch={filters.monthSearch}
            setMonthSearch={filters.setMonthSearch}
            facets={summary?.facets}
            filteredCount={filteredTotal}
            totalCount={summary?.monthTotal ?? 0}
            actionButtons={
              hasActionPanel ? (
                <div
                  className="nx-actions-row"
                  style={{
                    display: "flex",
                    gap: "10px",
                    flexWrap: "wrap",
                    paddingTop: "16px",
                    borderTop:
                      theme === "dark"
                        ? "1px solid rgba(255,255,255,0.1)"
                        : "1px solid rgba(0,0,0,0.1)",
                  }}
                >
                  <input
                    id="allowance-deduction-import-input"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: "none" }}
                    onChange={handleAllowanceDeductionFileSelect}
                  />
                  {(isAdmin || modulePermissions?.processPayroll) && (
                    <button
                      onClick={() => setShowProcessModal(true)}
                      style={{
                        ...getTopBarButtonStyle(theme, false),
                        background: "#2e7cba",
                        color: "#fff",
                        border: "none",
                      }}
                    >
                      <CreditCard size={16} />
                      {t("processPayroll")}
                    </button>
                  )}
                  {(isAdmin || modulePermissions?.recreatePayroll) && (
                    <button
                      onClick={() => {
                        setRecreateInitialCodes([]);
                        setShowRecreateModal(true);
                      }}
                      style={{
                        ...getTopBarButtonStyle(theme, false),
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
                      }}
                    >
                      <RefreshCw size={16} />
                      {t("recreatePayroll") || "Recreate Payroll"}
                    </button>
                  )}
                  {(isAdmin || (modulePermissions?.unpayPayroll ?? modulePermissions?.processPayroll)) && (
                    <button
                      onClick={() => setShowBulkUnpayModal(true)}
                      style={{
                        ...getTopBarButtonStyle(theme, false),
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
                      }}
                    >
                      <RotateCcw size={16} />
                      {t("bulkUnpay")}
                    </button>
                  )}
                  {(isAdmin || modulePermissions?.dataImport) && (
                    <button
                      onClick={handleShowImportDetails}
                      style={{
                        ...getTopBarButtonStyle(theme, false),
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
                      }}
                    >
                      <Upload size={16} />
                      {t("import")}
                    </button>
                  )}
                  {(isAdmin || (modulePermissions?.template ?? modulePermissions?.dataExport)) && (
                    <button
                      onClick={handleShowTemplateDetails}
                      style={{
                        ...getTopBarButtonStyle(theme, false),
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
                      }}
                    >
                      <Download size={16} />
                      {t("templateButton")}
                    </button>
                  )}
                  {filteredPayrollData && filteredPayrollData.length > 0 && (
                    <DownloadMenuButton
                      theme={theme}
                      isRTL={isRTL}
                      t={t}
                      onExcel={
                        isAdmin || (modulePermissions?.exportPayrollExcel ?? modulePermissions?.dataExport)
                          ? handleExportPayrollExcel
                          : null
                      }
                      onPdf={
                        isAdmin || (modulePermissions?.exportPayrollPDF ?? modulePermissions?.dataExport)
                          ? handleExportPayrollPDF
                          : null
                      }
                    />
                  )}
                  {(isAdmin || modulePermissions?.medicalInsurance) && (
                    <button
                      onClick={() => setShowMedicalInsuranceModal(true)}
                      style={{
                        ...getTopBarButtonStyle(theme, false),
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
                      }}
                    >
                      <Heart size={16} />
                      {t("medicalInsurance")}
                    </button>
                  )}
                </div>
              ) : null
            }
          />
        </div>

        {/* Bulk Selection Creative Toolbar */}
        <PayrollBulkActionBar
          t={t}
          theme={theme}
          currentTheme={currentTheme}
          isRTL={isRTL}
          selectedCount={selectedPayrollData.length}
          totalCount={filteredTotal}
          allSelected={allFilteredSelected}
          selectAllBusy={selectAllBusy}
          onSelectAll={handleToggleSelectAllFiltered}
          ignoredCount={selectedPayrollData.filter((e) => e.isIgnored).length}
          paidCount={selectedPayrollData.filter((e) => e.status === "paid").length}
          onClearSelection={handleClearPayrollSelection}
          onBulkIgnore={() => handleBulkToggleIgnore(true)}
          onBulkUnignore={() => handleBulkToggleIgnore(false)}
          onBulkUnpay={handleBulkUnpay}
          onOpenBulkAllowance={() => setShowBulkAllowanceModal(true)}
          onOpenBulkDeduction={() => setShowBulkDeductionModal(true)}
          onOpenBulkRecreate={() => {
            setRecreateInitialCodes(selectedPayrollData.map((e) => e.infinityCode).filter(Boolean));
            setShowRecreateModal(true);
          }}
          bulkIgnoreBusy={bulkIgnoreBusy}
          bulkUnpayBusy={bulkUnpayBusy}
          isAdmin={isAdmin}
          modulePermissions={modulePermissions}
        />

        {/* Table Card */}
        <div style={{ ...getCardStyle(theme), marginTop: "0px" }}>
          <PayrollTable
            t={t}
            styles={styles}
            theme={theme}
            language={language}
            currentTheme={currentTheme}
            fonts={fonts}
            spacing={spacing}
            screenSize={screenSize}
            currentItems={currentItems}
            hasMore={hasMore}
            serverPage={pagination?.page || 1}
            serverTotalPages={pagination?.totalPages || 1}
            onServerPageChange={payrollDataHook.setPage}
            visibleItems={filters.visibleItems}
            filteredPayrollData={filteredPayrollData}
            togglingIgnoredIds={payrollDataHook.togglingIgnoredIds}
            unpayingIds={payrollDataHook.unpayingIds}
            setAllowanceModal={setAllowanceModal}
            modulePermissions={modulePermissions}
            hasRowActions={hasRowActions}
            profile={profile}
            setSelectedEmployee={setSelectedEmployee}
            setDeductionModal={setDeductionModal}
            handleToggleIsIgnored={payrollDataHook.handleToggleIsIgnored}
            handleUnpayPayroll={payrollDataHook.handleUnpayPayroll}
            handleShowMore={handleShowMore}
            setDeductionTooltip={setDeductionTooltip}
            showAlert={showAlert}
            selectionEnabled={hasActionPanel}
            selectedIds={selectedPayrollIds}
            onToggleSelect={toggleSelectPayroll}
            allSelected={allFilteredSelected}
            someSelected={someFilteredSelected}
            onToggleSelectAll={handleToggleSelectAllFiltered}
          />
        </div>
      </div>

      {selectedEmployee && (
        <EmployeeDetailsModal
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
          currentTheme={currentTheme}
          t={t}
          language={language}
          theme={theme}
          isRTL={isRTL}
          showAlert={showAlert}
        />
      )}

      {showProcessModal && (
        <ProcessPayrollModal
          rowsLoading={monthRowsLoading}
          onClose={() => {
            setShowProcessModal(false);
            setProcessStep(1);
            setSelectedEmployees([]);
          }}
          onProcessComplete={() => {
            payrollDataHook.refreshPayrollData();
            setShowProcessModal(false);
            setProcessStep(1);
            setSelectedEmployees([]);
          }}
          payrollData={monthRows}
          selectedEmployees={selectedEmployees}
          setSelectedEmployees={setSelectedEmployees}
          selectedMonth={filters.selectedMonth}
          setSelectedMonth={filters.setSelectedMonth}
          theme={theme}
          language={language}
          API_BASE_URL={API_BASE_URL}
          clientName={clientName}
          showAlert={showAlert}
          processStep={processStep}
          setProcessStep={setProcessStep}
        />
      )}

      {showBulkUnpayModal && (
        <BulkUnpayModal
          isOpen={showBulkUnpayModal}
          onClose={() => setShowBulkUnpayModal(false)}
          payrollData={monthRows}
          rowsLoading={monthRowsLoading}
          currentTheme={currentTheme}
          t={t}
          onUnpayComplete={() => payrollDataHook.refreshPayrollData()}
          showAlert={showAlert}
          apiFetch={apiFetch}
          API_BASE_URL={API_BASE_URL}
        />
      )}

      {showRecreateModal && (
        <RecreatePayrollModal
          isOpen={showRecreateModal}
          onClose={() => {
            setShowRecreateModal(false);
            setRecreateInitialCodes([]);
          }}
          selectedMonth={filters.selectedMonth}
          currentTheme={currentTheme}
          theme={theme}
          language={language}
          isRTL={isRTL}
          t={t}
          showAlert={showAlert}
          apiFetch={apiFetch}
          API_BASE_URL={API_BASE_URL}
          clientName={clientName}
          initialSelectedInfinityCodes={recreateInitialCodes}
          onRecreateComplete={() => {
            payrollDataHook.refreshPayrollData();
            handleClearPayrollSelection();
          }}
        />
      )}

      {deductionModal.isOpen && (
        <DeductionsModal
          isOpen={deductionModal.isOpen}
          employee={deductionModal.employee}
          onClose={() => setDeductionModal({ isOpen: false, employee: null })}
          API_BASE_URL={API_BASE_URL}
          currentTheme={currentTheme}
          t={t}
          language={language}
          theme={theme}
          isRTL={isRTL}
          showAlert={showAlert}
        />
      )}
      {allowanceModal.isOpen && (
        <AllowancesModal
          isOpen={allowanceModal.isOpen}
          employee={allowanceModal.employee}
          onClose={() => setAllowanceModal({ isOpen: false, employee: null })}
          API_BASE_URL={API_BASE_URL}
          currentTheme={currentTheme}
          t={t}
          language={language}
          theme={theme}
          isRTL={isRTL}
          showAlert={showAlert}
          screenSize={screenSize}
        />
      )}

      {showBulkAllowanceModal && (
        <BulkAllowanceModal
          isOpen={showBulkAllowanceModal}
          onClose={() => setShowBulkAllowanceModal(false)}
          selectedEmployees={selectedPayrollData}
          API_BASE_URL={API_BASE_URL}
          currentTheme={currentTheme}
          t={t}
          language={language}
          theme={theme}
          isRTL={isRTL}
          showAlert={showAlert}
          onSuccess={({ keepSelection } = {}) => {
            payrollDataHook.refreshPayrollData();
            if (!keepSelection) handleClearPayrollSelection();
          }}
        />
      )}

      {showBulkDeductionModal && (
        <BulkDeductionModal
          isOpen={showBulkDeductionModal}
          onClose={() => setShowBulkDeductionModal(false)}
          selectedEmployees={selectedPayrollData}
          API_BASE_URL={API_BASE_URL}
          currentTheme={currentTheme}
          t={t}
          language={language}
          theme={theme}
          isRTL={isRTL}
          showAlert={showAlert}
          onSuccess={({ keepSelection } = {}) => {
            payrollDataHook.refreshPayrollData();
            if (!keepSelection) handleClearPayrollSelection();
          }}
        />
      )}

      {showImportModal && (
        <ExcelImportModal
          onClose={() => {
            setShowImportModal(false);
            resetImportState();
          }}
          onImportComplete={() => {
            payrollDataHook.refreshPayrollData();
            setShowImportModal(false);
            resetImportState();
          }}
          API_BASE_URL={API_BASE_URL}
          t={t}
          language={language}
          theme={theme}
          isRTL={isRTL}
          importStep={importStep}
          setImportStep={setImportStep}
          importFile={importFile}
          setImportFile={setImportFile}
          importData={importData}
          setImportData={setImportData}
          importLoading={importLoading}
          setImportLoading={setImportLoading}
          importError={importError}
          setImportError={setImportError}
          importProgress={importProgress}
          setImportProgress={setImportProgress}
          importResults={importResults}
          setImportResults={setImportResults}
          importSettings={importSettings}
          setImportSettings={setImportSettings}
        />
      )}

      <AllowanceDeductionImportProgress
        visible={adImportProgress.visible}
        minimized={adImportProgress.minimized}
        percent={adImportProgress.percent}
        etaSeconds={adImportProgress.etaSeconds}
        fileName={adImportProgress.fileName}
        language={language}
        theme={theme}
        t={t}
        onMinimize={handleMinimizeAdImportProgress}
        onExpand={handleExpandAdImportProgress}
        onClose={handleCloseAdImportProgress}
      />

      {adImportResultsOpen && (
        <AllowanceDeductionImportResultsModal
          data={adImportResultsData}
          onClose={closeAdImportResults}
          t={t}
          theme={theme}
          language={language}
          fonts={fonts}
        />
      )}

      {showMedicalInsuranceModal && (
        <MedicalInsuranceModal
          onClose={() => setShowMedicalInsuranceModal(false)}
          API_BASE_URL={API_BASE_URL}
          clientName={clientName}
          workingCompany={workingCompany}
          theme={theme}
          language={language}
        />
      )}

      {showTemplateDetailsModal && (
        <PayrollChoiceModal
          icon={FileSpreadsheet}
          title={t("templateButton")}
          subtitle={t("templateButtonTitle")}
          width={900}
          points={[t("templateDetailsFileName"), t("templateDetailsRequired"), t("templateDetailsAD")]}
          closeLabel={t("close")}
          onClose={handleCloseTemplateDetails}
          theme={theme}
          isRTL={isRTL}
          actions={[
            {
              key: "payroll",
              label: t("downloadPayrollTemplate"),
              icon: Download,
              variant: "primary",
              onClick: () => {
                // Same Template Builder as the A/D template — pick the
                // allowance/deduction columns, presets, search, filters.
                setShowTemplateDetailsModal(false);
                setTemplateBuilderMode("payroll");
                setShowTemplateBuilderModal(true);
              },
            },
            {
              key: "ad",
              label: t("downloadADTemplate"),
              icon: Download,
              variant: "success",
              onClick: () => {
                setShowTemplateDetailsModal(false);
                setTemplateBuilderMode("allowancesDeductions");
                setShowTemplateBuilderModal(true);
              },
            },
            { key: "all", label: t("downloadAllTemplates"), icon: Download, variant: "secondary", onClick: handleDownloadAllTemplates },
          ]}
        />
      )}

      {showImportDetailsModal && (
        <PayrollChoiceModal
          icon={Upload}
          title={t("import")}
          subtitle={t("importOptions")}
          points={[t("formatDescription")]}
          closeLabel={t("close")}
          onClose={handleCloseImportDetails}
          theme={theme}
          isRTL={isRTL}
          actions={[
            {
              key: "payroll",
              label: t("importExcel"),
              icon: FileSpreadsheet,
              variant: "primary",
              onClick: () => {
                resetImportState();
                setShowImportModal(true);
                setShowImportDetailsModal(false);
              },
            },
            {
              key: "ad",
              label: t("importAllowancesDeductions"),
              icon: Upload,
              variant: "success",
              onClick: () => {
                handleOpenAllowanceDeductionImport();
                setShowImportDetailsModal(false);
              },
            },
          ]}
        />
      )}

      {/* Template Builder Modal */}
      <TemplateBuilderModal
        key={templateBuilderMode}
        mode={templateBuilderMode}
        fullTemplateUrl={typeof templateFile === "string" ? templateFile : templateFile?.default}
        isOpen={showTemplateBuilderModal}
        onClose={() => setShowTemplateBuilderModal(false)}
        currentTheme={currentTheme}
        theme={theme}
        isRTL={isRTL}
        t={t}
        showAlert={showAlert}
        modulePermissions={modulePermissions}
        userRole={profile?.role || ""}
        screenSize={screenSize}
      />

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        showCancel={alertModal.showCancel}
        onConfirm={closeAlert}
        onClose={closeAlert}
        theme={theme}
        language={language}
      />

      <style>{`
        * { box-sizing: border-box; }
        html { scroll-behavior: smooth; overflow-x: clip; overflow-y: auto; }
        body { overflow-x: clip; overflow-y: auto !important; min-height: 100vh; margin: 0; padding: 0; }
        .modal-container { position: fixed; top: 0; left: 0; right: 0; bottom: 0; overflow-y: auto; -webkit-overflow-scrolling: touch; }
        .modal-overlay { backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
        .modal-close-btn { width: 36px; height: 36px; border-radius: 9999px; display: inline-flex; align-items: center; justify-content: center; transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .modal-close-btn:hover { transform: scale(1.06); box-shadow: 0 8px 20px rgba(2,6,23,0.24); }
        /* Payroll modals scroll without a visible scrollbar (same as the rest of the system). */
        .payroll-modal, .payroll-modal * { scrollbar-width: none; -ms-overflow-style: none; }
        .payroll-modal::-webkit-scrollbar, .payroll-modal *::-webkit-scrollbar { display: none; width: 0; height: 0; }
        /* Directional icons (back / next arrows) point the other way in Arabic. */
        [dir="rtl"] .payroll-rtl-flip { transform: scaleX(-1); }
      `}</style>
    </div>
  );
};

export default ProfessionalPayrollSystem;
